import { AppStrings } from "../../utils/constants/AppStrings";
import { formatCombinedOdds } from "../../utils/BetTableUtils";
import { convertToUserTimezone } from "../../utils/dateService";
import type {
  GamePassUserRedeemedBet,
  TaskListRecord,
} from "../../types/gamePass";
import { TASK_DATA_SOURCE } from "../../types/gamePass";
import { normalizeCasinoGameId } from "./casinoGameNormalizers";
import type {
  GamePassTaskBetViewModel,
  GamePassUserBet,
  GamePassUserBetLeg,
  GamePassUserBetsData,
  MapGamePassUserBetOptions,
} from "./taskBetTypes";

const MONEY_TYPE_LABELS: Record<string, string> = {
  rebet_cash: "RCH",
  rebet_coin: "RC",
  p_cash: "PC",
};

const CASINO_CATEGORY_LABELS: Record<string, string> = {
  TABLE_GAMES: "Table Games",
  LIVE_CASINO: "Live Casino",
  SLOTS: "Slots",
  JACKPOT: "Jackpot",
};

const formatMoneyTypeLabel = (moneyType?: string): string => {
  if (!moneyType) {
    return "";
  }
  return MONEY_TYPE_LABELS[moneyType] ?? moneyType;
};

/** Same instant parsing as User Manager tables: unix seconds (int or legacy
 *  float string), ISO, or Date. Admin timezone. Includes time so Date Settled
 *  matches `created_at` instead of falling back to a date-only / placed time. */
const BET_TIMESTAMP_DISPLAY_FORMAT = "MMM D, YYYY, HH:mm:ss";

const formatBetTimestamp = (value?: string | number | null): string => {
  const numeric = typeof value === "number" ? value : Number(value);
  if (
    value == null ||
    value === "" ||
    value === "0" ||
    (Number.isFinite(numeric) && numeric <= 0)
  ) {
    return AppStrings.NOT_AVAILABLE;
  }

  const formatted = convertToUserTimezone(
    value,
    BET_TIMESTAMP_DISPLAY_FORMAT,
  );
  return !formatted || formatted === "-"
    ? AppStrings.NOT_AVAILABLE
    : formatted;
};

const getBetResult = (
  won?: string,
): Pick<GamePassTaskBetViewModel, "resultLabel" | "resultVariant"> => {
  if (won === "1") {
    return { resultLabel: AppStrings.WON, resultVariant: "won" };
  }
  if (won === "0") {
    return { resultLabel: AppStrings.LOST, resultVariant: "lost" };
  }
  return { resultLabel: AppStrings.PENDING, resultVariant: "pending" };
};

export const formatCompactBetId = (betId: string): string => {
  const normalized = betId.replace(/-/g, "");
  if (!normalized) {
    return betId;
  }
  return `Bet-${normalized.slice(0, 8)}`;
};

const getLegs = (bet: GamePassUserBet): ReadonlyArray<GamePassUserBetLeg> =>
  bet.bets?.length ? bet.bets : [];

const getFirstLeg = (bet: GamePassUserBet): GamePassUserBetLeg | undefined =>
  getLegs(bet)[0];

const getCompetitors = (
  bet: GamePassUserBet,
): GamePassUserBetLeg["competitors"] => {
  if (bet.competitors?.length) {
    return bet.competitors;
  }

  for (const leg of getLegs(bet)) {
    if (leg.competitors?.length) {
      return leg.competitors;
    }
  }

  return undefined;
};

const getCompetitorMatchup = (
  competitors?: GamePassUserBetLeg["competitors"],
): string => {
  if (!Array.isArray(competitors) || competitors.length === 0) {
    return "";
  }

  const home = competitors.find((entry) => entry.qualifier === "home")?.name;
  const away = competitors.find((entry) => entry.qualifier === "away")?.name;
  if (home && away) {
    return `${home} vs ${away}`;
  }

  return competitors
    .map((entry) => entry.name)
    .filter(Boolean)
    .join(" vs ");
};

const getAwayTeamName = (
  competitors?: GamePassUserBetLeg["competitors"],
): string =>
  competitors?.find((entry) => entry.qualifier === "away")?.name?.trim() ?? "";

const getSelectionLabel = (
  selection?: GamePassUserBet["selection"],
): string => {
  const outcomes = selection?.market?.outcome;
  if (!Array.isArray(outcomes)) {
    return "";
  }

  const chosenOutcome = outcomes.find(
    (outcome) => outcome.chosen === 1 || outcome.chosen === "1",
  );
  return (
    chosenOutcome?.easy_read_line?.trim() ??
    chosenOutcome?.name?.trim() ??
    selection?.market?.market_name?.trim() ??
    ""
  );
};

const getBetSelection = (bet: GamePassUserBet): GamePassUserBet["selection"] => {
  if (bet.selection) {
    return bet.selection;
  }

  for (const leg of getLegs(bet)) {
    if (leg.selection) {
      return leg.selection;
    }
  }

  return undefined;
};

const getLeagueFallbackLabel = (bet: GamePassUserBet): string =>
  bet.league_name?.trim() ??
  bet.league?.trim() ??
  getFirstLeg(bet)?.league_name?.trim() ??
  getFirstLeg(bet)?.league?.trim() ??
  AppStrings.NOT_AVAILABLE;

const isMultiLegBet = (bet: GamePassUserBet): boolean =>
  Boolean(bet.is_parlay || bet.is_grouped_bet || (bet.bets?.length ?? 0) > 1);

const getSportLabel = (bet: GamePassUserBet): string => {
  if (bet.is_parlay) {
    return "Parlay";
  }
  if (bet.is_grouped_bet) {
    return "SGP";
  }

  const sportName = bet.sport_name?.trim() ?? getFirstLeg(bet)?.sport_name?.trim();
  if (sportName) {
    return sportName;
  }

  const leagueLabel = getLeagueFallbackLabel(bet);
  if (leagueLabel !== AppStrings.NOT_AVAILABLE) {
    return leagueLabel;
  }

  return AppStrings.NOT_AVAILABLE;
};

const buildSingleSelectionLabel = (
  selection: string,
  competitors?: GamePassUserBetLeg["competitors"],
): string => {
  const matchup = getCompetitorMatchup(competitors);
  const awayTeam = getAwayTeamName(competitors);

  if (selection) {
    if (selection.toLowerCase().includes(" vs ")) {
      return selection;
    }
    if (awayTeam && selection !== awayTeam) {
      return `${selection} vs ${awayTeam}`;
    }
    if (matchup) {
      return `${selection} (${matchup})`;
    }
    return selection;
  }

  return matchup;
};

const buildLegSelectionLabels = (bet: GamePassUserBet): string[] => {
  const legs = getLegs(bet);
  if (legs.length === 0) {
    const selection = buildSingleSelectionLabel(
      getSelectionLabel(bet.selection),
      getCompetitors(bet),
    );
    return selection ? [selection] : [];
  }

  return legs
    .map((leg) =>
      buildSingleSelectionLabel(
        getSelectionLabel(leg.selection),
        leg.competitors,
      ),
    )
    .filter(Boolean);
};

const buildSelectionDetailLabel = (bet: GamePassUserBet): string => {
  if (isMultiLegBet(bet)) {
    return buildSubmittedDescription(bet);
  }

  const legLabels = buildLegSelectionLabels(bet);
  if (legLabels.length > 0) {
    return legLabels.join("; ");
  }

  return getLeagueFallbackLabel(bet);
};

export const buildSubmittedDescription = (bet: GamePassUserBet): string => {
  const matchup = getCompetitorMatchup(getCompetitors(bet));

  if (isMultiLegBet(bet)) {
    const legCount =
      bet.num_legs && bet.num_legs > 0
        ? bet.num_legs
        : bet.bets?.length ?? 0;
    const typeLabel = bet.is_grouped_bet ? "SGP" : "Parlay";
    if (legCount > 0) {
      return matchup
        ? `${legCount}-leg ${typeLabel} (${matchup})`
        : `${legCount}-leg ${typeLabel}`;
    }
    return matchup ? `${typeLabel} (${matchup})` : typeLabel;
  }

  const legLabels = buildLegSelectionLabels(bet);
  if (legLabels.length > 0) {
    return legLabels[0] ?? AppStrings.NOT_AVAILABLE;
  }

  if (matchup) {
    return matchup;
  }

  return getLeagueFallbackLabel(bet);
};

const getOddsLabel = (bet: GamePassUserBet): string => {
  const topLevelOdds = bet.combined_odds ?? bet.odds;
  if (topLevelOdds != null && topLevelOdds !== "") {
    const formatted = formatCombinedOdds(topLevelOdds);
    if (formatted !== "-") {
      return formatted;
    }
  }

  for (const leg of getLegs(bet)) {
    const legOdds = leg.selection?.market?.outcome?.find(
      (outcome) => outcome.chosen === 1 || outcome.chosen === "1",
    )?.odds;
    if (legOdds != null && legOdds !== "") {
      const formatted = formatCombinedOdds(legOdds);
      if (formatted !== "-") {
        return formatted;
      }
    }
  }

  const chosenOdds = getBetSelection(bet)?.market?.outcome?.find(
    (outcome) => outcome.chosen === 1 || outcome.chosen === "1",
  )?.odds;

  if (chosenOdds != null && chosenOdds !== "") {
    const formatted = formatCombinedOdds(chosenOdds);
    if (formatted !== "-") {
      return formatted;
    }
  }

  return AppStrings.NOT_AVAILABLE;
};

const getSubmittedHighlightLabel = (bet: GamePassUserBet): string =>
  getOddsLabel(bet);

export const isSportsbookTask = (task?: TaskListRecord | null): boolean => {
  if (!task?.data_source) {
    return true;
  }
  return task.data_source === TASK_DATA_SOURCE.SPORTSBOOK;
};

export const isCasinoTask = (task?: TaskListRecord | null): boolean =>
  task?.data_source === TASK_DATA_SOURCE.CASINO;

export const shouldShowTaskBetPanel = (task?: TaskListRecord | null): boolean => {
  if (!task?.data_source) {
    return true;
  }
  return (
    task.data_source === TASK_DATA_SOURCE.SPORTSBOOK ||
    task.data_source === TASK_DATA_SOURCE.CASINO
  );
};

export const isCasinoBet = (
  bet: GamePassUserBet,
  taskDataSource?: string,
): boolean => {
  if (taskDataSource === TASK_DATA_SOURCE.CASINO) {
    return true;
  }

  if (bet.bet_type === "3") {
    return true;
  }

  const source = bet.source?.trim().toLowerCase() ?? "";
  if (source === "casino") {
    return true;
  }

  const hasCasinoFields = Boolean(
    bet.game_name?.trim() || bet.game_id?.trim() || bet.provider?.trim(),
  );
  const hasSportsbookFields = Boolean(
    bet.sport_name?.trim() ||
      bet.league?.trim() ||
      bet.league_name?.trim() ||
      bet.selection ||
      bet.is_parlay ||
      bet.is_grouped_bet ||
      (bet.bets?.length ?? 0) > 0,
  );

  return hasCasinoFields && !hasSportsbookFields;
};

const humanizeCasinoCategory = (category?: string): string => {
  const normalized = category?.trim();
  if (!normalized) {
    return "";
  }

  return (
    CASINO_CATEGORY_LABELS[normalized] ??
    normalized
      .replace(/_/g, " ")
      .split(/\s+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ")
  );
};

const getCasinoGameTypeLabel = (bet: GamePassUserBet): string => {
  const gameType = bet?.game_type?.trim()?.length ? bet?.game_type?.trim() : bet?.provider_name?.trim();
  if (gameType) {
    return gameType;
  }

  const categoryLabel = humanizeCasinoCategory(bet.category);
  if (categoryLabel) {
    return categoryLabel;
  }

  return AppStrings.NOT_AVAILABLE;
};

const getCasinoGameNameLabel = (
  bet: GamePassUserBet,
  casinoGameNameLabels?: Readonly<Record<string, string>>,
): string => {
  const apiName = bet.game_name?.trim();
  if (apiName) {
    return apiName;
  }

  const gameId = normalizeCasinoGameId(bet.game_id);
  if (!gameId) {
    return AppStrings.NOT_AVAILABLE;
  }

  // FE-2284: casino bets frequently arrive without `game_name`; resolve the
  // id against the admin casino-games catalogue instead of printing it raw.
  const resolvedName = casinoGameNameLabels?.[gameId]?.trim();
  if (resolvedName && resolvedName !== gameId) {
    return resolvedName;
  }

  return gameId;
};

/** Casino game ids on the given bets that have no display name of their own
 *  and therefore need a catalogue lookup (FE-2284). */
export const collectUnresolvedCasinoGameIds = (
  bets: ReadonlyArray<GamePassUserBet> | undefined,
  taskDataSource?: string,
): string[] => {
  const ids = new Set<string>();
  for (const bet of bets ?? []) {
    if (!isCasinoBet(bet, taskDataSource) || bet.game_name?.trim()) {
      continue;
    }
    const gameId = normalizeCasinoGameId(bet.game_id);
    if (gameId) {
      ids.add(gameId);
    }
  }
  return Array.from(ids);
};

const formatCurrencyAmountLabel = (
  amount?: number | string,
  moneyType?: string,
): string => {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount)) {
    return AppStrings.NOT_AVAILABLE;
  }

  const currencyLabel = formatMoneyTypeLabel(moneyType);
  const formattedAmount = Number.isInteger(numericAmount)
    ? String(numericAmount)
    : numericAmount.toFixed(2);

  return currencyLabel
    ? `${formattedAmount} ${currencyLabel}`
    : formattedAmount;
};

const getCasinoBetAmountLabel = (bet: GamePassUserBet): string =>
  formatCurrencyAmountLabel(
    bet.wager ?? bet.wager_amount ?? bet.amount,
    bet.money_type,
  );

const formatCasinoPayoutHighlight = (bet: GamePassUserBet): string => {
  if (bet.won !== "1") {
    return AppStrings.NOT_AVAILABLE;
  }

  const payout = Number(bet.payout);
  if (Number.isFinite(payout)) {
    const currencyLabel = formatMoneyTypeLabel(bet.money_type);
    const formattedPayout = payout.toFixed(2);
    return currencyLabel
      ? `+${formattedPayout} ${currencyLabel}`
      : `+${formattedPayout}`;
  }

  const wagerAmount = Number(bet.wager ?? bet.wager_amount ?? bet.amount);
  if (!Number.isFinite(wagerAmount)) {
    return AppStrings.NOT_AVAILABLE;
  }

  return formatCurrencyAmountLabel(wagerAmount, bet.money_type);
};

const mapCasinoBetToViewModel = (
  bet: GamePassUserBet,
  options: MapGamePassUserBetOptions = {},
): GamePassTaskBetViewModel => {
  const betId = bet.bet_id?.trim() ?? "";
  const result = getBetResult(bet.won);
  const gameNameLabel = getCasinoGameNameLabel(
    bet,
    options.casinoGameNameLabels,
  );

  return {
    betId,
    compactBetId: formatCompactBetId(betId),
    betKind: "casino",
    sportLabel: AppStrings.NOT_AVAILABLE,
    selectionLabel: AppStrings.NOT_AVAILABLE,
    oddsLabel: AppStrings.NOT_AVAILABLE,
    gameTypeLabel: getCasinoGameTypeLabel(bet),
    gameNameLabel,
    betAmountLabel: getCasinoBetAmountLabel(bet),
    submittedDescription: gameNameLabel,
    submittedHighlightLabel: formatCasinoPayoutHighlight(bet),
    resultLabel: result.resultLabel,
    resultVariant: result.resultVariant,
    placedDateLabel: formatBetTimestamp(bet.created_at),
    submittedDateLabel: formatBetTimestamp(bet.settled_at),
  };
};

export const mapGamePassUserBetToViewModel = (
  bet: GamePassUserBet,
  options: MapGamePassUserBetOptions = {},
): GamePassTaskBetViewModel | null => {
  const betId = bet.bet_id?.trim();
  if (!betId) {
    return null;
  }

  if (isCasinoBet(bet, options.taskDataSource)) {
    return mapCasinoBetToViewModel(bet, options);
  }

  const result = getBetResult(bet.won);
  const oddsLabel = getOddsLabel(bet);

  return {
    betId,
    compactBetId: formatCompactBetId(betId),
    betKind: "sportsbook",
    sportLabel: getSportLabel(bet),
    selectionLabel: buildSelectionDetailLabel(bet),
    oddsLabel,
    gameTypeLabel: AppStrings.NOT_AVAILABLE,
    gameNameLabel: AppStrings.NOT_AVAILABLE,
    betAmountLabel: AppStrings.NOT_AVAILABLE,
    submittedDescription: buildSubmittedDescription(bet),
    submittedHighlightLabel: getSubmittedHighlightLabel(bet),
    resultLabel: result.resultLabel,
    resultVariant: result.resultVariant,
    placedDateLabel: formatBetTimestamp(bet.created_at),
    submittedDateLabel: formatBetTimestamp(bet.settled_at),
  };
};

export const mapGamePassUserBetsDataToViewModels = (
  betsData?: GamePassUserBetsData,
  options: MapGamePassUserBetOptions = {},
): GamePassTaskBetViewModel[] =>
  (betsData?.bets ?? [])
    .map((bet) => mapGamePassUserBetToViewModel(bet, options))
    .filter((bet): bet is GamePassTaskBetViewModel => bet != null);

const normalizeBetId = (betId: string): string => betId.trim().toLowerCase();

export const filterAndOrderBetsByRedeemedIds = (
  bets: ReadonlyArray<GamePassTaskBetViewModel>,
  redeemedBetIds: ReadonlyArray<string>,
): GamePassTaskBetViewModel[] => {
  if (redeemedBetIds.length === 0) {
    return [];
  }

  const betById = new Map(
    bets.map((bet) => [normalizeBetId(bet.betId), bet]),
  );

  return redeemedBetIds
    .map((betId) => betById.get(normalizeBetId(betId)))
    .filter((bet): bet is GamePassTaskBetViewModel => bet != null);
};

export const mapProgressRedeemedBetsToViewModels = (
  redeemedBets: ReadonlyArray<GamePassUserRedeemedBet> | undefined,
  redeemedBetIds: ReadonlyArray<string>,
  options: MapGamePassUserBetOptions = {},
): GamePassTaskBetViewModel[] => {
  if (!redeemedBets?.length || redeemedBetIds.length === 0) {
    return [];
  }

  const betById = new Map<string, GamePassUserRedeemedBet>();
  for (const bet of redeemedBets) {
    const betId = bet.bet_id?.trim();
    if (betId) {
      betById.set(normalizeBetId(betId), bet);
    }
  }

  return redeemedBetIds
    .map((betId) => {
      const redeemedBet = betById.get(normalizeBetId(betId));
      return redeemedBet
        ? mapGamePassUserBetToViewModel(redeemedBet, options)
        : null;
    })
    .filter((bet): bet is GamePassTaskBetViewModel => bet != null);
};
